import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ModuleManager } from "src/shared/models/module-manager";
import { IModuleManagerMenuItem, ModuleManagerMenuItemType, ModuleManagerSubMenu } from "src/shared/models/module-manager/module-manager-menu-item.model";
import { ChartView, DisplayCheckItem, ModuleView, SpecialGroupName } from "./cxone-metrics-breakdown.models";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { AgGridHelperService } from "src/app/reusables/services/ag-grid-helper.service";
import { ModuleThemesService } from "src/app/reusables/services/module-themes.service";
import { AgContextMenuService } from "src/app/reusables/services/ag-context-menu.service";
import { NavigationService } from "src/app/navigation/navigation.service";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { CvConfigurationService } from "src/app/reusables/services/cv-configuration.service";
import { AgGridEvent, ColDef, ColumnVisibleEvent, GridOptions, ValueFormatterParams } from "ag-grid-community";
import { WidgetThresholdsModalService } from "src/app/dashboard/modals/widget-thresholds-modal/widget-thresholds-modal.service";
import { ModuleManagerMetricInfo } from "src/shared/models/module-manager/module-manager-settings.models";
import { MetricBreakDownService } from "./metrics-breakdown.service";
import { CurrentAgentStatesService } from "src/app/dashboard/modules/current-agent-states/current-agent-states.service";
import { NumericType } from "src/shared/models/pipeline-config/metric-definition.models";
import { AgGridAngular } from "ag-grid-angular";
import Highcharts from 'highcharts';
import HC_more from "highcharts/highcharts-more";

@Component({
    selector: 'metrics-breakdown',
    templateUrl: './metrics-breakdown.component.html',
    styleUrls: ['./metrics-breakdown.component.scss']
})
export class MetricsBreakDownComponent implements OnInit, OnDestroy {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @Input() metrics: ModuleManagerMetricInfo[] = [];
    @ViewChild("searchBox", { read: ElementRef }) searchBox: ElementRef<HTMLInputElement>;
    chartObject: Highcharts.Chart = null;
    @ViewChild('metricsBreakdownWidgetGrid') agGrid: AgGridAngular;

    moduleID: number;
    isFirstLoad = true;
    isAborting = false;
    data: any = null;
    dataLoading = false;
    dataLoadPromise: any = null;
    abortLoad: () => void = null;
    dataLoadSuccess = false;
    isManualRefresh = false;
    initialSettingsApplied = false;
    isFirstLoadTable = true;
    firstLoad = true;
    isNoMetric = false;
    isDateRangeTooWideIntraday = false;
    localizationResult = '';
    searchPlaceholder = '';
    isAgentStateDetailModalOpen = false;
    public highcharts = Highcharts;
    public configHighChart: Highcharts.Options;
    colors = ["#99CABD", "#4959A4", "#9F8BC7", "#6A46A4", "#4F8192", "#9DA953"];
    public displayItems: any[] = [];

    //View helpers
    singleMode = true;
    dualMode = false;

    //State Management
    moduleState = {
        name: "metricBreakdownState",
        autoLoad: true,
        initialLoad: false,
        view: ModuleView.Chart,
        chartView: ChartView.Legend,
        legendPosition: "right",
        upfrontView: false,
        themeIndex: 0,
        legendDisplay: true
    };
    //
    // for chart component
    //
    preventLoadingModal = false; // Set if data is reloading via auto refresh
    chartType = (this.moduleState.view != null) ? this.moduleState.view : ModuleView.Chart;
    legendItemsHidden = new Map();
    chartConfig = null;
    chartPadding = {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    };
    
    currentTheme: string[] = null;
    modalRef: NgbModalRef = null;

    ///
    /// For Table Component
    ///
    footerRow = {};
    filterValue = "";
    searchOpen = false;
    tableState = null;
    showFooter = true;
    gridReady = false;
    parentVisible = false;
    agentInfoVisible = false;
    stateSaveTimer = null;
    params: any = null;
    stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"
    firstAnimateComplete = false;
    noData = false;
    gridOptions: GridOptions = null;
    displayCheckItems: DisplayCheckItem[] = [];
    isCXOne = false;
    component: any;
    metricName: string;
    defaultColumnWidth = 200;
    localeText: any;

    constructor(
        public service: MetricBreakDownService,
        public cvLocalizationService: CVLocalizationService,
        public agGridHelperService: AgGridHelperService,
        public moduleThemesService: ModuleThemesService,
        public config: CvConfigurationService,
        public agContextMenuService: AgContextMenuService,
        public navigationService: NavigationService,
        public ngbModal: NgbModal,
        public thresholdService: WidgetThresholdsModalService
    ) {
        this.isCXOne = config.isCxOne;
    }

    get isResizeWaiting() {
        return this.moduleManager?.container.isResizeWaiting;
    }
    setChartInstance(event) {
        this.chartObject = event;
    }

    setUpGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions(
            {
                context: this.moduleManager,
                components: {
                    groupRenderer:
                        this.agGridHelperService.GroupDrillDownCellRenderer,
                    groupHeaderComponent:
                        this.agGridHelperService.GroupHeaderComp,
                },
                accentedSort: true,
                animateRows: true,
                suppressCellFocus: true,
                suppressScrollOnNewData: true,
                suppressContextMenu: true,
                tooltipInteraction: true,

                // grid data
                defaultColDef: {
                    minWidth: this.agGridHelperService.defaultMinWidth,
                },
                columnDefs: [],
                rowData: [],

                // grid events
                onGridReady: event => {
                    this.gridReady = true;
                    this.stateRestoreInProgress = true; // Prevent saving while the widget is still loading the initial states
                    if (this.tableState) {
                        this.onTableStateLoaded(this.tableState);
                    }  else if (this.moduleState.view === ModuleView.Table || this.moduleState.view === ModuleView.Dual) {
                        this.updateTableState();
                    }
                    this.applyFilter();
                    this.restoreColumnState(); //Needed to restore columns size saved on the tableState.
                    setTimeout(() => (this.stateRestoreInProgress = false), 0); // Wait for the next tick to let events finish before allowing state save
                },
                getRowId: (params) => params.data.Group.groupID,
                onSortChanged: () => this.queueStateSave(),
                onColumnMoved: () => this.queueStateSave(),
                onColumnPinned: () => this.queueStateSave(),
                onColumnVisible: (e: ColumnVisibleEvent) => {
                    if (e.source === "api") return;
                    if (e.columns?.length === 1 && e.columns[0].getColDef().hide !==!e.columns[0].isVisible()) {
                        const columnDefinition: ColDef = this.gridOptions.columnDefs.find((c: ColDef) => c.field === e.columns[0].getColId());
                        columnDefinition.hide = !e.columns[0].isVisible();
                    }
                    this.agGridHelperService.sizeColumnsSmart(e);
                    this.queueStateSave();
                },
                onGridSizeChanged: (event) => {
                    if (this.isResizeWaiting) {
                        this.agGridHelperService.sizeColumnsSmart(event);
                        this.queueStateSave();
                    } else if (this.moduleManager.container.isFullscreen) {
                        this.agGridHelperService.sizeColumnsSmart(event);
                    } else {
                        this.restoreColumnState();
                    }
                    this.chartObject?.reflow();
                },
                onColumnResized: (event) => {
                    if (event.source == "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
                        this.queueStateSave();
                    }
                },
                overlayNoRowsTemplate: this.config.isCxOne
                    ? `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.cvLocalizationService.getCachedText(
                            "Error_Module",
                            "CXOne_No_Results",
                            "No results found"
                        )}
                    </p>
                </div>`
                    : null,
                onFilterChanged: () => this.isResizeWaiting ? this.queueStateSave() : null,
                onCellClicked: (params) => (this.params = params),
                suppressDragLeaveHidesColumns: true,
                localeText: this.localeText,
            },
            {
                enableCopy: true,
            }
        );
    }

    tableStateChanged() {
        this.tableState = this.agGridHelperService.getGridState(this.agGrid.api);            
        this.queueStateSave();
    }

    async ngOnInit() {
        this.localeText = await this.agGridHelperService.getLocalizations();
        this.setUpGrid();
        HC_more(this.highcharts);
        this.moduleID = this.moduleManager.container.moduleInfo.DTO?.ModuleID;
        this.moduleManager.showLoader = true;
        //Set numeric metric types that should be disabled in the selector
        this.moduleManager.moduleSettings.disabledNumericMetrics = [
            NumericType.Percent,
        ];
        if(this.gridOptions) {
            this.gridOptions.context = {
                moduleManager: this.moduleManager,
            };
        }

        await this.cvLocalizationService.GetSection("Common");
        await this.cvLocalizationService.GetSection("Error_Module");
        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");
        await this.cvLocalizationService.GetSection("Summary_Module");
        this.displayCheckItems = [
            {
                displayName: this.getLocaleText("Show_Totals"),
                checked: true,
            },
            {
                displayName: this.getLocaleText("Show_Parent_Level"),
                checked: this.parentVisible,
            },
            {
                displayName: this.getLocaleText("Show_Agent_Info"),
                checked: this.agentInfoVisible,
            },
        ];

        this.moduleManager.setOptions({
            onSettingsApplied: async (settings) =>  await this.handleSettingApplied(settings),
            hasRefresh: true,
            onImageExport: () => this.exportPNG(),
            imageExportVisible: () => this.moduleState.view !== ModuleView.Table,
            onRefresh: async (isAutoRefresh) => {
                await this.loadData(isAutoRefresh);
                this.populateData();
            },
            canRefresh: () => !this.dataLoading,
        });

        this.moduleManager.setOptions({
            onExport: (format, isCXOne) => this.service.tableLoadPerformanceDataForExport(this.moduleKey, format, isCXOne, this.moduleID)
        });

        this.moduleManager.addState({
            name: "metricBreakdownState",
            autoLoad: true,
            getStateData: () => this.moduleState,
            onStateLoaded: async (stateData) => {
                if (stateData) {
                    await this.handleOnStateLoaded(stateData);
                }
            }
        });
        if (!this.moduleManager.hasWriteAccess() && !this.isCXOne) {
            this.agGridHelperService.setGridReadOnly(this.gridOptions);
        }
        if (this.gridOptions?.columnDefs) {
            this.gridOptions.columnDefs.forEach((el: ColDef) => {
                if (typeof el.field === 'string') {
                    if (el.field === "parentGroupData") {
                        this.parentVisible = !el.hide;
                        return;
                    }
                    if (el.field.startsWith("AAgent")) {
                        this.agentInfoVisible = !el.hide;
                        return;
                    }
                }
            });
        }
        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: (stateData) => {
                this.tableState = stateData;
                if (this.gridReady) {
                    this.onTableStateLoaded(stateData);
                }
            },
            autoLoad: true,
        });
        this.moduleManager.addState({
            name: "footerVisible",
            getStateData: () => this.showFooter,
            onStateLoaded: (stateData) =>
                this.footerVisibleOnStateLoad(stateData),
            autoLoad: true,
        });
        this.moduleManager.registerEvents({
            onDashboardActivated: () => {
                if (this.agGrid?.api && this.gridReady) {
                    setTimeout(() => {
                        this.agGridHelperService.sizeColumns(
                            this.agGrid?.api,
                            this.gridOptions
                        );
                    }, 500);
                }
            },
        });
        this.moduleManager.registerEvents({
            onResize: () => window.setTimeout(() => this.chartObject?.reflow(), 500),
            onResizeStop: () => window.setTimeout(() => this.chartObject?.reflow(), 500),
            onFullscreenToggle: () => window.setTimeout(() => this.chartObject?.reflow(), 500),
        });
    }
    
    footerVisibleOnStateLoad(stateData) {
        if (stateData === undefined) {
            return;
        }
        this.showFooter = stateData;
        this.displayCheckItems[0].checked = this.showFooter;
        this.setFooterVisible(this.showFooter);
    }

    async handleSettingApplied(settings) {
        if (!settings?.selectedMetrics?.length && this.moduleManager.getModuleInfo().ModuleID !== 144) {
            return;
        }
        this.moduleState.initialLoad = true;
        await this.loadData(false);
        if (this.agGrid?.api) this.agGrid?.api.refreshHeader();
        this.populateData();
    }

    handleOnStateLoaded(stateData) {
        if (!stateData) {
            this.setModuleState(ModuleView.Chart, ChartView.Legend);
            return;
        }
        this.moduleState = stateData;
        if (this.moduleState.view) {
            this.setModuleState(this.moduleState.view, this.moduleState.chartView);
        } else {
            this.setModuleState(ModuleView.Chart, this.moduleState.chartView);
        }

        if (this.moduleState.view === ModuleView.Table || this.moduleState.view === ModuleView.Dual) {
            this.menuItemsForTable();
        }
        this.moduleState.initialLoad = true;
    }

    getLocaleText(key: string, defaultValue?: string) {
        return this.cvLocalizationService.getCachedText("Summary_Module", key, defaultValue);
    }

    populateData() {
        // Run when finished loading
        if ((this.data && this.moduleState.view === ModuleView.Table) || this.moduleState.view === ModuleView.Dual) {
            this.comboLoadDataTable(this.data);
            this.applyFilter();
        }

        // Run when finished loading
        if ((this.data && this.moduleState.view === ModuleView.Chart) || this.moduleState.view === ModuleView.Dual) {
            this.comboLoadDataChart(this.data);
        }
    }

    resetMenuItems() {
        this.moduleManager.resetMenu();

        // assure first load has search icon on table or dual
        if (this.moduleState.view === ModuleView.Table || this.moduleState.view === ModuleView.Dual) {
            this.moduleManager.addHeaderItem({icon: "search", click: () => this.toggleOpenSearch(), name: "searchIcon"});
        }
        this.setComboMenu();
        // assures first load has table menu items
        if (this.moduleState.view !== ModuleView.Chart) {
            this.menuItemsForTable();
        }
        if (this.moduleState.view === ModuleView.Chart || this.moduleState.view === ModuleView.Dual) {
            this.menuItemsForChart();
        }
    }

    menuItemsForTable() {
        this.moduleManager.addMenuItem({
            name: this.getLocaleText("Table Display"),
            icon: "tableDisplay",
            getEnabled: () =>
                this.agGrid?.api && this.gridOptions.columnDefs.length > 0,
            type: ModuleManagerMenuItemType.CheckMenu,
            getCheckItems: () => this.handleCheckedItemsOnMenuForTable(),
            getItemName: (i) => this.displayCheckItems[i].displayName,
            isItemChecked: (i) => this.displayCheckItems[i].checked,
            onCheckClicked: (i) => {
                this.displayCheckItems[i].checked = !this.displayCheckItems[i].checked;
                this.displayCheckItemClicked(i);
            },
            readOnlyEnabled: true,
        } as IModuleManagerMenuItem);
    }

    handleCheckedItemsOnMenuForTable() {
        if (this.data) {
            const index = this.data.rows[0]?.attributeValue.length ===this.data.columnDefs.length? 2: 1;
            if (this.data.columnDefs[index + 1].name === "AAgent ID")
                return this.displayCheckItems;
            return this.displayCheckItems.slice(0, 2);
        }
    }

    // this gets called when the view changes from chart table or dual
    toggleHeaderFilterButton(activeView: string) {
        if (activeView === ModuleView.Chart && this.moduleManager.additionalHeaderItems !== null) {
            delete this.moduleManager.additionalHeaderItems["searchIcon"];
        } else {
            this.moduleManager.addHeaderItem({icon: "search", click: () => this.toggleOpenSearch(), name: "searchIcon"});
        }
    }

    abortLoading() {
        this.isAborting = true;
        this.abortLoad?.();
        this.dataLoadPromise = null;
    }

    handleDataErrorOne() {
        this.isNoMetric = true;
        this.dataLoading = false;
        this.moduleManager.showLoader = false;
        this.dataLoadPromise = false;
        this.dataLoadSuccess = true;
        this.isManualRefresh = false;
        this.resetMenuItems();
    }

    handleDataErrortwo() {
        this.isDateRangeTooWideIntraday = true;
        this.dataLoading = false;
        this.moduleManager.showLoader = false;
        this.dataLoadPromise = false;
        this.dataLoadSuccess = true;
        this.isManualRefresh = false;
        this.resetMenuItems();
    }

    async loadData(isAutoRefresh) {
        if (this.dataLoadPromise) {
            this.abortLoading();
        }
        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }

        this.dataLoading = true;
        const abort = new Promise<void>((resolve, reject) => (this.abortLoad = () => resolve()));
        this.dataLoadPromise = this.service.LoadPerformanceData(this.moduleKey, isAutoRefresh, this.isFirstLoad, abort);
        try {
            this.data = await this.dataLoadPromise;
            if (this.data.Error === 1) {
                this.handleDataErrorOne();
                return;
            } else if (this.data.Error === 2) {
                this.handleDataErrortwo();
                return;
            }
        } catch (e) {
            if (this.isAborting || e?.code === DOMException.ABORT_ERR || e?.statusText === "abort") {
                this.isAborting = false;
                return;
            }
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        }
        this.isNoMetric = false;
        this.isDateRangeTooWideIntraday = false;
        this.resetMenuItems();
        this.dataLoadSuccess = true;
        this.moduleManager.showLoader = false;
        this.moduleManager.setAutoRefreshReady();
        // Reset loader flags
        this.dataLoading = false;
        this.dataLoadPromise = null;
        this.isManualRefresh = false;
        this.isFirstLoad = false;
        this.applyFilter();
    }

    setComboMenu() {
        this.displayItems = [];
        const options = this.moduleManager.getModuleInfo().options;
        if (!options?.tableAvailable || !options?.chartAvailable) return;

        if (options.tableAvailable) {
            this.displayItems.push({
                name: this.getLocaleText("Table"),
                icon: "tableView",
                getEnabled: () => true,
                click: () => {
                    this.setModuleState(ModuleView.Table);
                    this.toggleHeaderFilterButton(ModuleView.Table);
                },
            });
        }
        if (options.chartAvailable) {
            this.displayItems.push({
                name: this.getLocaleText("Chart"),
                icon: "chartView",
                getEnabled: () => true,
                click: () => {
                    this.setModuleState(ModuleView.Chart);
                    this.toggleHeaderFilterButton(ModuleView.Chart);
                },
            });
        }
        if (options.tableAndChartAvailable) {
            this.displayItems.push({
                name: this.getLocaleText("Table_Chart"),
                icon: "tableAndChartView",
                getEnabled: () => true,
                click: () => {
                    this.moduleState.legendPosition = "bottom";
                    this.setModuleState(ModuleView.Dual);
                    this.toggleHeaderFilterButton(ModuleView.Dual);
                },
            });
        }
        this.moduleManager.addMenuItem({
            name: this.getLocaleText("Widget_Display"),
            icon: "chartType",
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.dataLoadSuccess,
            subItems: this.displayItems,
            readOnlyEnabled: true
        } as ModuleManagerSubMenu);

        this.moduleManager.addMenuItem({
            name: this.getLocaleText("Widget_View"),
            icon: "widgetView",
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.chartObject != null && this.moduleState.view  !== ModuleView.Table,
            subItems: [
                {
                    name: this.getLocaleText("Show_Legend"),
                    icon: "",
                    click: () => {
                        this.showLegend(true, false);
                        this.setModuleState(this.moduleState.view, ChartView.Legend);
                    }
                },
                {
                    name: this.getLocaleText("Show_Data_Upfront"),
                    icon: "",
                    click: () => {
                        this.showLegend(false, true);
                        this.setModuleState(this.moduleState.view, ChartView.DataLabels);
                    }
                },
            ],
        } as ModuleManagerSubMenu);
    }

    menuItemsForChart() {
        if (!this.moduleState.upfrontView) {
            this.moduleManager.addMenuItem({
                name: this.getLocaleText("Summary_Module", "Legend_Position"),
                icon: "legend",
                type: ModuleManagerMenuItemType.SubMenu,
                getEnabled: () =>
                    !this.moduleState.upfrontView &&
                    !this.noData,
                subItems: [
                    {
                        name: this.getLocaleText("Top"),
                        icon: "positionTop",
                        click: () => this.setLegendPosition("top", true),
                    },
                    {
                        name: this.getLocaleText("Bottom"),
                        icon: "positionBottom",
                        click: () => this.setLegendPosition("bottom", true),
                    },
                    {
                        name: this.getLocaleText("Left"),
                        icon: "positionLeft",
                        click: () => this.setLegendPosition("left", true),
                    },
                    {
                        name: this.getLocaleText("Right"),
                        icon: "positionRight",
                        click: () => this.setLegendPosition("right", true),
                    },
                    {
                        type: 'divider'
                    },
                    {
                        name: this.moduleState.legendDisplay ? this.getLocaleText('Hide_Legend_Chart') : this.getLocaleText('Show_Legend_Chart'),
                        icon: '',
                        click: () => this.moduleState.legendDisplay ? this.showLegend(false, false) : this.showLegend(true, false)
                    }
                ],
                readOnlyEnabled: true
            } as ModuleManagerSubMenu);
        }
    }

    async showLegend(showLegend: boolean, showUpfrontView: boolean) {
        if (this.moduleState.legendDisplay === showLegend
            && this.moduleState.upfrontView === showUpfrontView) {
            return;
        }
        this.moduleState.legendDisplay = showLegend;
        this.moduleState.upfrontView = showUpfrontView;
        this.menuItemsForChart();
        this.createChart();
        await this.moduleManager.saveState('metricBreakdownState');
        this.moduleManager.requestPublishNeeded();
    }

    // States
    setModuleState(state, chartState = ChartView.Legend) {
        if (this.moduleState.view === state) {
            return;
        }
        this.moduleManager.resetMenu();
        this.setComboMenu();
        this.moduleState.view = state;
        switch (this.moduleState.view) {
            case ModuleView.Table:
                this.singleMode = true;
                this.dualMode = false;
                this.moduleState.view = ModuleView.Table;
                this.menuItemsForTable();
                break;
            case ModuleView.Chart:
                this.singleMode = true;
                this.dualMode = false;
                this.moduleState.view = ModuleView.Chart;
                this.moduleState.chartView = chartState;
                this.moduleState.upfrontView = this.moduleState.chartView === ChartView.Legend? false: true;
                this.menuItemsForChart();
                this.createChart();
                break;
            case ModuleView.Dual:
                this.singleMode = false;
                this.dualMode = true;
                this.moduleState.view = ModuleView.Dual;
                this.moduleState.chartView = chartState;
                this.moduleState.upfrontView = this.moduleState.chartView === ChartView.Legend? false: true;
                this.menuItemsForChart();
                this.menuItemsForTable();
                this.createChart();
                break;
            default:
                this.singleMode = true;
                this.dualMode = false;
                break;
        }
        if (this.data) {
            this.populateData();
        }
        this.moduleManager.saveState('metricBreakdownState');
        this.moduleManager.requestPublishNeeded();
    }

    /**
     * Called when table state is loaded in. Often, this will happen after the grid is ready, but for this widget, the column defs come from
     * the data. So it may be triggered from onStateLoaded of tableState, or onGridReady. Whichever function is last makes this call.
     *
     * @param stateData State data that is brought in. If this is being called from onStateLoaded, it will pass in the value found there. Otherwise
     *  it will use this.tableState (which is a copy of the value from onStateLoaded)
     * @returns void
     */
    onTableStateLoaded(stateData) {
        if (!stateData || Object.keys(stateData).length === 0) {
            // No table state loaded
            this.prepareTableState();
            return;
        }

        this.tableState.columns.forEach((el, i) => {
            // Location or Parent
            if (el.name === "parentGroupData") {
                if (el.visible) {
                    this.parentVisible = true;
                    for (const checkItem of this.displayCheckItems) {
                        if (
                            checkItem.displayName ===
                            this.getLocaleText("Show_Parent_Level")
                        ) {
                            checkItem.checked = this.parentVisible;
                            break;
                        }
                    }
                }
            }
        });
        this.restoreColumnState();
    }

    comboLoadDataChart(data) {
        if (!data) {
            this.noData = true;
        } else {
            this.noData = false;
        }
        this.setLegendPosition(this.moduleState.legendPosition, false);
        this.createChart();
    }

    comboLoadDataTable(data) {
        if (!data || data.rows?.length === 0) {
            this.noData = true;
            return;
        }

        this.data = data;
        this.noData = false;
        if (this.data.Error === 1 || this.data.Error === 2) {
            return;
        }

        this.updateColumnDefs();
        const rowData = [];
        let index = 0;
        if (this.data.row != null && this.data.row.length >= 1) {
            index = this.data.rows[0].attributeValue.length === this.data.columnDefs.length? 1: 0;
        }
        for (let i = 0; i < this.data.rows.length; i++) {
            const newRow = {
                ID: i,
                Group: this.data.rows[i].group,
                "AAgent ID": this.data.rows[i].attributeValue[index + 1],
                "AAgent Email": this.data.rows[i].attributeValue[index + 2],
                groupData: this.data.rows[i].group,
                moduleKey: this.moduleKey,
                groupLevel: this.moduleManager.moduleSettings.selectedGroupLevel,
                parentGroupData: this.data.rows[i].locationGroup, // FIX would need to change cSharpSide here for name parentGroup renamed from location to parent
                parentGroupLevel: this.data.rows[i].locationGroup.groupLevel,
            };
            let j;
            for (j = 1; j < this.data.rows[i].metricData.length; j++) {
                try {
                    const currentMetric = this.data.rows[i].metricData[j];
                    currentMetric.MetricType = this.data.rows[i].metricTypes[j];
                    currentMetric.MetricDisplayValue =
                        this.data.rows[i].metricData[j].MetricDisplayValue;
                    if (currentMetric.MetricType === 2) {
                        currentMetric.MetricValue = parseFloat(
                            (currentMetric.MetricValue * 100).toFixed(2)
                        );
                    }
                    currentMetric.value = currentMetric.MetricType === 3? currentMetric.MetricDisplayValue: this.data.rows[i].metricData[j].MetricValue;
                    newRow["Metric" + currentMetric.MetricID] = currentMetric;
                } catch (ex) {
                    // The metric does not exist or does not have a MetricID
                    // This is generally okay; blank cells aren't given an associated MetricID in the endpoint
                }
            }
            for (j = 1; j < this.data.rows[i].attributeValue.length; j++) {
                const currentAttrID = this.data.rows[i].attributeIDs[j];
                const currentAttr = this.data.rows[i].attributeValue[j];
                if (currentAttrID > 0 && currentAttr != null)
                    newRow["UAttribute" + currentAttrID] = currentAttr;
            }
            rowData.push(newRow);
        }

        this.gridOptions.rowData = rowData;
        if (this.agGrid?.api) {
            // if the grid is already loaded, we have to use the api to notify agGrid
            this.agGrid?.api.setGridOption("rowData", rowData);
        }
    }

    ngOnDestroy(): void {
        this.modalRef?.dismiss();
    }

    async setLegendPosition(position, recreateChart) {

        if (this.moduleState.legendPosition === position && this.moduleState.legendDisplay === true) {
            return;
        }

        // Set position string
        this.moduleState.legendPosition = position;
        switch (position) {
            case "top":
                this.chartPadding = {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
            case "bottom":
                this.chartPadding = {
                    top: 16,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
            case "left":
                this.chartPadding = {
                    top: 16,
                    bottom: 8,
                    left: 0,
                    right: 0,
                };
                break;
            case "right": // Extra top and bottom space
                this.chartPadding = {
                    top: 16,
                    bottom: 8,
                    left: 0,
                    right: 0,
                };
                break;
            default: // Same as 'top'
                this.chartPadding = {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                };
                break;
        }

        // Apply changes
        // Moving the legend requires the chart to be completely recreated instead of just updated.
        // This method (using a variable rather than direct manipulation) also allows the padding values to be retained if the chart is recreated in any way.
        if (recreateChart) {
            this.moduleState.legendDisplay = true;
            this.menuItemsForChart();
            this.createChart();
            await this.moduleManager.saveState('metricBreakdownState');
            this.moduleManager.requestPublishNeeded();
        }
    }

    formatNumberAbbreviated(value: number): string {
        if (isNaN(value)) {
            return "";
        }
        if (typeof value === "string") {
            value = parseFloat(value);
        }
        const billion = 1e9;
        const million = 1e6;
        const thousand = 1e3;

        if (value >= billion) {
            return (value / billion).toFixed(2) + " B";
        } else if (value >= million) {
            return (value / million).toFixed(2) + " M";
        } else if (value >= thousand) {
            return (value / thousand).toFixed(2) + " K";
        } else if (value === 0) {
            return value.toString();
        }
        else if (value < 1) {
            return value.toFixed(2);
        } else {
            let decimalIndex = value.toString().indexOf(".");
            if (decimalIndex === -1) {
                return value.toString();
            }
            return value.toFixed(2).toString();
        }
    }
 
    prepareDataSet() {
        const preppedDatasets = [];
        const datasetGroupNames = [];
        if (!this.data || !this.data.rows || this.data.rows.length === 0 || this.data === "") {
            this.noData = true;
            return { preppedDatasets: [], datasetGroupNames: [] };
        } else {
            let other = 0;
            let colorIndex = 0;
            const labels = [];
            const metricValues = [];

            if (this.config.isCxOne) {
                this.currentTheme = this.colors;
            }

            this.data.rows.sort((a: any, b: any) => {
                const valueA = a.metricData[1]?.MetricValue as number;
                const valueB = b.metricData[1]?.MetricValue as number;
                return valueB - valueA;
            });
            for (let i = 0; i < this.data.rows.length; i++) {
                if (this.data.rows[i].metricTypeNames[1] === "Attribute") {
                    continue;
                }
                const groupName = this.data.rows[i].group.groupName;
                labels.push(groupName);
                const metricVal = this.data.rows[i].metricData[1]?.MetricValue;
                const metricDisplayVal = this.data.rows[i].metricData[1]?.MetricDisplayValue ?? "";

                if (metricVal > 0) {
                    metricValues.push(metricVal);
                    if (preppedDatasets.length === 0) {
                        datasetGroupNames.push(groupName);
                        preppedDatasets.push({
                            backgroundColor: this.currentTheme,
                            data: [metricVal],
                            displayValues: [metricDisplayVal],
                        });
                        colorIndex++;
                        if (colorIndex >= this.currentTheme.length) {
                            colorIndex = 0;
                        }
                    } else {
                        if (preppedDatasets[0].data.length < 5) {
                            datasetGroupNames.push(groupName);
                            preppedDatasets[0].data.push(metricVal);
                            preppedDatasets[0].displayValues.push(metricDisplayVal);
                        } else {
                            other += metricVal;
                        }
                    }
                }
            }

            if (other > 0) {
                datasetGroupNames.push(SpecialGroupName.Other);
                preppedDatasets[0].data.push(other);
                preppedDatasets[0].displayValues.push(other.toString());
            }
            if (preppedDatasets.length > 0) {
                preppedDatasets[0].displayValues = preppedDatasets[0]?.displayValues?.map(value => parseFloat(value).toFixed(2));
                return { preppedDatasets, datasetGroupNames };
            } else {
                this.noData = true;
                return { preppedDatasets: [], datasetGroupNames: [] };
            }
        }
    }

    createChart() {
        if (this.moduleManager.getModuleSettings().selectedMetrics?.length > 0) {
            this.metricName = this.moduleManager.getModuleSettings().selectedMetrics[0]?.Name;
        }
        const { preppedDatasets, datasetGroupNames } = this.prepareDataSet();
        const updateCustomLabel = (chart, total) => {
            var textX = chart.plotWidth / 2 + chart.plotLeft;
            var textY = chart.plotHeight / 2 + chart.plotTop;
            const largeFontSize =
                Math.min(chart.plotWidth, chart.plotHeight) * 0.1;
            if (chart.customLabel1) {
                chart.customLabel1.destroy();
            }

            chart.customLabel1 = chart.renderer.text(this.formatNumberAbbreviated(total), textX, textY + 40)
                .css({
                    color: "#000000",
                    fontSize: largeFontSize + "px",
                    lineHeight: "12px",
                    fontWeight: 600,
                    "font-family": "Open Sans",
                    textAlign: "center",
                })
                .attr({
                    align: "center",
                })
                .add();

            chart.customLabel1.attr({
                x: textX,
                y: textY + 2,
            });
        };
        if (preppedDatasets.length > 0 && this.metricName && this.moduleState.upfrontView !== undefined) {
            const moduleManager = this.moduleManager;
            this.configHighChart = {
                chart: {
                    type: "pie",
                    backgroundColor: "transparent",
                    events: {
                        render() {
                            const chart = this;
                            const totalVisible = chart.series[0].data.reduce((sum, point) => sum + (point.visible ? point.y : 0), 0);
                            updateCustomLabel(chart, totalVisible);
                        },
                        load() {
                            const chart = this;
                            chart.reflow();
                        },
                        redraw() {
                            const chart = this;
                            chart.reflow();
                        }
                    },
                    marginTop: this.moduleState.upfrontView && !this.moduleState.legendDisplay ? -20 : null,
                    spacingTop: this.moduleState.upfrontView && !this.moduleState.legendDisplay ? null : 40,
                    spacingBottom: this.moduleState.upfrontView && !this.moduleState.legendDisplay ? null : 40
                },
                title: {
                        text: this.metricName,
                        align: "center",
                        style: {
                            fontWeight: "bold",
                            fontSize: "16px",
                        },
                    },
                    exporting: {
                        enabled: false,
                    },
                    credits: {
                        enabled: false,
                    },
                tooltip: {
                        useHTML: true,
                        backgroundColor: '#fff',
                        shadow: false,
                        formatter: function () {
                            const point = this.point as Highcharts.Point;
                            const moduleName = moduleManager.getModuleSettings().selectedMetrics[0].Name;

                            return `
                                 <div style="display: flex; flex-direction: column; align-items: start; z-index: 99999;">
                                    <div style="display: flex; align-items: center; margin-bottom: 4px;">
                                        <div style="width: 12px; height: 12px; background-color: ${point.color}; border-radius: 50%; margin-right: 5px;"></div>
                                        <div style="font-weight: bold;">${point.name}</div>
                                    </div>
                                    <div>${moduleName}: <b>${point.y.toFixed(2)}</b></div>
                                    <div>Percentage contribution: <b>${point.percentage.toFixed(2)}%</b></div>
                                </div>
                            `;
                        },
                        style: {
                            color: "#000",
                            fontFamily: "Open Sans",
                            fontSize: "13px",
                            fontWeight: "400",
                            lineHeight: "20px",
                            zIndex: 99999,
                        },
                    },
                    plotOptions: {
                        pie: {
                            allowPointSelect: false,
                            animation: false,
                            states: {
                                hover: {
                                    enabled: false,
                                    brightness: 0,
                                },
                            },
                            dataLabels: {
                                enabled: this.moduleState.upfrontView,
                                style:{
                                    zIndex: 1
                                },
                            },
                            innerSize: "60%",
                            showInLegend: !this.moduleState.upfrontView,
                            size: this.moduleState.upfrontView ? "60%" : null,
                        },
                    },
                    legend: {
                        enabled: this.moduleState.legendDisplay,
                        layout: this.moduleState.legendPosition === "top" || this.moduleState.legendPosition === "bottom" ? "horizontal" : "vertical",
                        align: this.moduleState.legendPosition === "left" ? "left" : this.moduleState.legendPosition === "right" ? "right" : "center",
                        verticalAlign: this.moduleState.legendPosition === "top" ? "top" : this.moduleState.legendPosition === "bottom" ? "bottom" : "middle",
                        symbolHeight: 12,
                        symbolWidth: 12,
                        symbolPadding: 4,
                        symbolRadius: 0,
                        itemDistance: 6,
                        itemMarginTop: 5,
                        itemMarginBottom: 5,
                        itemStyle: {
                        fontSize: "12px",
                        fontFamily: "Open Sans",
                        fontWeight: "400",
                        color: "#000",
                        lineHeight: "14.52px",
                        cursor: "pointer",
                        textOverflow: "ellipsis",
                    },
                    labelFormatter: function () {
                        const fixedFontSize = 12;
                        const point = this as Highcharts.Point; // Access the current point context
                        const percentage = point.percentage.toFixed(2); // Get percentage
                        return `<span style="font-size: ${fixedFontSize}px;">${this.name} (${percentage}%)</span>`;
                    },
                    margin: 0,
                    x: 0,
                    y: this.moduleState.legendPosition === "top"? 20: this.moduleState.legendPosition === "bottom"? -20: 0,
                },
                series: [
                    {
                        type: "pie",
                        name: "Metric Breakdown",
                        states: {
                            hover: {
                                enabled: false,
                            },
                            inactive: {
                                opacity: 1,
                            },
                        },
                        dataLabels: {
                            enabled: this.moduleState.upfrontView,
                            useHTML: false,
                            formatter: function () {
                                if (this.percentage >= 5) {
                                    return `${this.percentage.toFixed(1)}% <br/>
                                            ${this.point.name} (${this.y.toFixed(2)})`;
                                }
                                return null;
                            },
                            style: {
                                color: "black",
                                textOutline: "none",
                                fontSize: "13px",
                                zIndex: 1,
                                fontWeight: 'normal'
                            },
                            distance: 20,
                            connectorWidth: 1,
                            connectorColor: "black",
                            verticalAlign: "middle",
                            crop: true,
                            connectorShape: "fixedOffset",
                        },
                        data: datasetGroupNames.map((name, index) => ({
                            name,
                            y: preppedDatasets[0].data[index],
                            color: this.colors[index % this.colors.length],
                            displayValue: preppedDatasets[0].displayValues[index],
                        })),
                    },
                ],
                responsive: {
                    rules: [],
                },
            };
        } else {
            this.noData = true;
        }
        this.firstAnimateComplete = true;
        requestAnimationFrame(() => this.chartObject?.reflow());
    }

    exportPNG() {
        const date = new Date().toISOString().slice(0, 19).replace(/:/g, "_").replace("T", "_");
        const fileName =
            this.cvLocalizationService
            .getCachedText(
                "Module_Names",
                "CXone_SkillSummary",
                "CXone_SkillSummary"
            )
            .replace(/\s+/g, "-")
            .toLowerCase() +
            "_widget_(" +
            date +
            ").png";
    
        if (this.chartObject) {
            // Extract SVG from the chart's container
            const svgElement = this.chartObject.container.querySelector('svg');
            if (svgElement) {
                const svgData = new XMLSerializer().serializeToString(svgElement);
                const svgDataUri = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    
                const canvas = document.createElement("canvas");
                const context = canvas.getContext("2d");
                const image = new Image();
    
                image.onload = () => {
                    canvas.width = image.width;
                    canvas.height = image.height;
                    context?.drawImage(image, 0, 0);
    
                    const imgData = canvas.toDataURL("image/png");
                    const link = document.createElement("a");
                    link.setAttribute("download", fileName);
                    link.setAttribute("href", imgData);
                    link.click();
    
                    URL.revokeObjectURL(link.href);
                    link.remove();
                };
    
                image.src = svgDataUri;
            }
        }
    }

    displayCheckItemClicked(i) {
        switch (i) {
            case 0: // Show Totals
                this.toggleFooterVisible();
                break;
            case 1:
                this.toggleParentVisible();
                break;
        }
    }

    sizeColumns() {
        if (!this.data) {
            return;
        }
        if (this.moduleManager.moduleSettings.selectedMetrics.length > 0)
            this.agGridHelperService.sizeColumns(this.agGrid?.api, this.gridOptions);
        if (this.tableState?.filterStates) {
            this.agGrid?.api.setFilterModel(this.tableState.filterStates);
        }
    }

    sizeColumnsSmart(event: AgGridEvent) {
        // If gridReady, or clientWidth or clientHeight missing, use the generic sizing. Otherwise (most cases), use the 'smart' sizing.
        if (event.type === "gridReady" || !event["clientWidth"] || !event["clientHeight"]) {
            this.sizeColumns();
        } else {
            this.agGridHelperService.sizeColumnsSmart(event);
        }
    }

    restoreColumnState() {
        if (!this.tableState?.columns ||!this.agGrid?.api ||this.agGrid?.api.getColumnDefs().length < 1) {
            return;
        }
        this.agGridHelperService.loadGridState(this.agGrid?.api, this.tableState);
    }

    updateColumnDefs() {
        const columnDefs = [];
        const totalFooterRow = {
            ID: 'total',
            Group: {
                groupID: 'total',
                groupName: 'Totals',
            },
        };
        const avgFooterRow = {
            ID: 'avg',
            Group: {
                groupID: 'avg',
                groupName: 'Averages',
            },
        };

        // tracking variables for seeing if we need to update columns
        let numMatchedDefs = 0;
        let newColumnFound = false;
        const oldColumnDefMarker = {};
        this.gridOptions.columnDefs.forEach((colDef: ColDef) => {
            oldColumnDefMarker[colDef.field] = true;
        });

        // Parent
        if (!this.data.columnDefs.some(itm => itm.field === 'parentGroupData')) {
            this.data.columnDefs.splice(1, 0, {
                field: 'parentGroupData',
                displayName: "Parent",
                name: "",
                customName: "",
                metricID: 0,
                width: "",
                minWidth: "",
                type: "",
                enablePinning: false,
                enableHiding: false,
                footerCellTemplate: "",
                totalValue: 0,
                total: "",
                avgValue: 0,
                avg: "",
                numericType: 0
            });
        }

        // go through the returned column definitions and turn them into an object for agGrid
        const metricsByField = {};
        let index = 0;
        for (const column of this.data.columnDefs) {
            const ugColDef = column;
            let agColDef;
            if (ugColDef.displayName === "Group") {
                agColDef = {
                    headerName: ugColDef.displayName,
                    headerComponent: 'groupHeaderComponent',
                    field: ugColDef.name,
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'groupRenderer',
                    pinnedRowCellRenderer: this.agGridHelperService.basicCellRenderer,
                    type: 'groupColumn'
                };
            } else if (ugColDef.displayName === "Parent") { // Location NOW called Parent
                agColDef = {
                    headerName: this.getLocaleText('Parent_Label'),
                    field: "parentGroupData",
                    filter: 'agTextColumnFilter',
                    hide: !this.parentVisible,
                    type: "groupColumn",
                    pinnedRowCellRenderer: () => "",
                    valueFormatter: (params: ValueFormatterParams) => {
                        if (params?.node?.data?.ID === "total") {
                            return "";
                        }

                        return params?.node?.data?.parentGroupData?.groupName
                            ? params.node.data.parentGroupData.groupName
                            : this.getLocaleText('No_Parent');
                    },
                };
            } else if (ugColDef.name.startsWith('AAgent')) {
                agColDef = {
                    headerName: ugColDef.displayName,
                    field: ugColDef.name,
                    hide: !this.agentInfoVisible
                };
            } else if (ugColDef.name.startsWith('UAttribute')) {
                const isRenamed = ugColDef.customName && ugColDef.customName !== "";
                agColDef = {
                    headerName: isRenamed ? ugColDef.customName + "*" : ugColDef.displayName,
                    field: ugColDef.name,
                    type: 'ellipsesColumn',
                };
            } else {
                const isRenamed = ugColDef.customName && ugColDef.customName !== "";
                agColDef = {
                    headerName: isRenamed ? ugColDef.customName + "*" : ugColDef.displayName,
                    headerTooltip: isRenamed ?
                        this.cvLocalizationService.getCachedText(
                            "Common", "Original_Metric", "Original Metric Name") + ": " + "\n" + ugColDef.displayName : null,
                    field: ugColDef.name,
                };
            }

            // check if this column was in our column defs before
            if (oldColumnDefMarker[agColDef.field] === true) {
                numMatchedDefs++;
            } else {
                newColumnFound = true;
            }

            // add the metric specific functions for handling MetricDisplayValue and MetricValue as well as performance objectives
            const isMetric = ugColDef.metricID > 0;
            if (isMetric) {
                agColDef.type = 'metricColumn';
                agColDef.cellRenderer = 'metricMenuRenderer';
                agColDef.cellRendererParams = {
                    isCXOne: this.config.isCxOne,
                    groupType: this.data.rows[0]?.group?.groupType,
                    groupLevel: this.data.rows[0]?.group?.groupLevel,
                };
                metricsByField[agColDef.field] = {
                    MetricID: ugColDef.metricID,
                    NumericType: ugColDef.numericType,
                    TimeType: ugColDef.timeType,
                };

                // add this columns total to footer
                totalFooterRow[agColDef.field] = {
                    value: ugColDef.totalValue,
                    MetricID: ugColDef.metricID,
                    MetricValue: ugColDef.totalValue,
                    MetricDisplayValue: ugColDef.total,
                };
                avgFooterRow[agColDef.field] = {
                    value: ugColDef.avgValue,
                    MetricID: ugColDef.metricID,
                    MetricValue: ugColDef.avgValue,
                    MetricDisplayValue: ugColDef.avg,
                };

            } else if (ugColDef.field === 'parentGroupData') { // Parent
                totalFooterRow[agColDef.field] = "";
                avgFooterRow[agColDef.field] = "";
            } else { // not a metric (group or attribute)
                // add the text from total into this footer row
                totalFooterRow[agColDef.field] = { groupName: this.isCXOne ? this.getLocaleText('Total') : this.getLocaleText('Totals'), groupID: "total" };
                avgFooterRow[agColDef.field] = { groupName: this.getLocaleText('Averages'), groupID: "avg" };
            }

            columnDefs.push(agColDef);
            index++;
        }
        this.gridOptions.context.getMetric = params => metricsByField[params.colDef.field];

        // update footer data
        this.footerRow = totalFooterRow;
        if (this.isCXOne && this.showFooter || !this.isCXOne) { // only put on the grid
            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('pinnedBottomRowData', [totalFooterRow]);
            } else {
                this.gridOptions.pinnedBottomRowData = [totalFooterRow];
            }
        }
        else if (!this.isCXOne) {
            if (this.agGrid?.api)
                this.agGrid?.api.setGridOption('pinnedBottomRowData', [avgFooterRow]);
            else
                this.gridOptions.pinnedBottomRowData = [avgFooterRow];
        }

        // update the column defs on the grid only if we need to
        let columnOrderChange = 0;
        if (columnDefs.length === this.gridOptions.columnDefs.length) {
            for (let j = 2; j < this.gridOptions.columnDefs.length; j++) {
                if (columnDefs[j].headerName !== this.gridOptions.columnDefs[j].headerName) {
                    columnOrderChange++;
                }
            }
        }

        let newMetricAdded = false;
        if (this.metrics?.toString() !== this.moduleManager.moduleSettings.selectedMetrics?.toString()) {
            this.metrics = this.moduleManager.moduleSettings.selectedMetrics;
            newMetricAdded = true;
        }

        if (newColumnFound || numMatchedDefs < this.gridOptions.columnDefs.length || columnOrderChange > 0 || newMetricAdded) {
            this.gridOptions.columnDefs = columnDefs;
            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('columnDefs', this.gridOptions.columnDefs);
                if (this.tableState?.filterStates) {
                    this.agGrid?.api.setFilterModel(this.tableState.filterStates);
                }
                if (this.tableState) {
                    this.agGridHelperService.sizeColumnsFromApisSmart(this.agGrid?.api);
                    this.agGridHelperService.loadGridState(this.agGrid.api, this.tableState);
                    this.tableStateChanged();
                }
            }
            return true;
        }
        return false;
    }

    applyFilter() {
        if (this.filterValue === "" && this.showFooter) {
            this.setFooterVisible(true);
        } else {
            this.setFooterVisible(false);
        }
        if (this.agGrid?.api) {
            // notify filter change
            this.agGrid?.api.setGridOption("quickFilterText", this.filterValue.trim());
            if (this.searchOpen && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0) {
                this.agGrid?.api?.showNoRowsOverlay();
            } else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    }

    clearFilter() {
        this.filterValue = "";

        if (this.showFooter) {
            this.setFooterVisible(true);
        }

        if (this.agGrid?.api) {
            // notify filter change
            this.agGrid?.api.setGridOption("quickFilterText", this.filterValue);
            this.applyFilter();
        }
    }

    toggleOpenSearch() {
        if (this.searchOpen) {
            this.searchOpen = false;
            this.clearFilter();
        } else {
            this.searchOpen = true;
        }
    }
    /**
     * Combines state saves within a second of each other into one save
     */
    queueStateSave() {
        if (this.stateRestoreInProgress) return;
        this.isFirstLoadTable = false;


        if (this.stateSaveTimer !== false) {
            clearTimeout(this.stateSaveTimer);
        }

        this.stateSaveTimer = setTimeout(() => {
            this.updateTableState();
            this.stateSaveTimer = false;
        }, 500);
    }

    prepareTableState() {
        if (!this.agGrid?.api) {
            return;
        }

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid?.api);
        if (tableState != null) {
            this.tableState = tableState;
        }
    }

    async updateTableState() {
        if (!this.agGrid?.api || this.stateRestoreInProgress) {
            return;
        }
        this.prepareTableState();

        // tell module manager to save the state
        if (await this.moduleManager.saveState("tableState")) {
            this.moduleManager.requestPublishNeeded();
        }
    }

    toggleFooterVisible() {
        this.showFooter = !this.showFooter;
        this.setFooterVisible(this.showFooter, true);
    }

    setFooterVisible(visible, save = false) {
        const footerToShow = [];
        this.showFooter = visible;

        if (visible) {
            footerToShow.push(this.footerRow);
        }

        if (this.agGrid?.api) {
            this.agGrid?.api.setGridOption('pinnedBottomRowData', footerToShow);
        } else {
            this.gridOptions.pinnedBottomRowData = footerToShow;
        }

        // setPinnedBottomRowData was reverting groupType and groupLevel so im ensuring its set correctly here
        this.gridOptions.columnDefs.forEach((col: any) => {
            if (col.type === "metricColumn") {
                col.cellRendererParams = {
                    isCXOne: this.config.isCxOne,
                    groupType: this.data.rows[0]? this.data.rows[0].group.groupType: "",
                    groupLevel: this.data.rows[0]? this.data.rows[0].group.groupLevel: "",
                };
            }
        });
        this.agGrid?.api?.setGridOption("columnDefs", this.gridOptions.columnDefs);

        if (save) {
            this.moduleManager.saveState("footerVisible");
            this.moduleManager.requestPublishNeeded();
        }
    }

    toggleParentVisible(skipSave = false) {
        this.parentVisible = !this.parentVisible;

        for (const checkItem of this.displayCheckItems) {
            if (checkItem.displayName === this.getLocaleText("Show_Parent_Level")) {
                checkItem.checked = this.parentVisible;
                break;
            }
        }

        for (const column of this.gridOptions.columnDefs) {
            const columnDefinition: ColDef = column;
            if (columnDefinition.field === "parentGroupData") {
                columnDefinition.hide = !this.parentVisible;
                this.agGrid?.api.moveColumn("parentGroupData", this.agentInfoVisible ? 3 : 1);
                this.agGrid?.api.setColumnVisible("parentGroupData", this.parentVisible);
                if (!skipSave && this.gridReady) {
                    this.prepareTableState();
                    this.queueStateSave();
                }
                return;
            }
        }
    }
}
